// src/components/galleries/NFTGallery.js
// ------------------------------------------------------------
//  Handles badge-detail modal, NFT detail, and bridge flow
// ------------------------------------------------------------

import React, { useState, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';

import NFTExplorerView   from '../views/NFTExplorerView';
import NFTMyNFTsView     from '../views/NFTMyNFTsView';
import NFTAnalyticsView  from '../views/NFTAnalyticsView';
import NFTDetailModal    from '../modals/NFTDetailModal';
import BadgeDetailModal  from '../modals/BadgeDetailModal';
import BridgeModal       from '../modals/BridgeModal';
import BridgeSelectionBar from '../BridgeSelectionBar';

import { useNFTGallery } from '../../hooks/useNFTGallery';
import { useBridge }     from '../../hooks/useBridge';

const NFTGallery = ({
  baseURI,
  contract,
  publicContract,
  etherlinkPublicContract,
  account,
  isAdmin,
  refreshGallery,
  confirmedAdminMints,
  view,
  accountCreated,
  sogniClient,
  isSogniLoggedIn,
  handleSogniLogin,
  sogniUsername,
  onRequireLogin,
  // Bridge-related props from App.js
  signer,
  onBridgeStateChange,
}) => {
  const gallery = useNFTGallery({
    baseURI,
    contract,
    publicContract,
    account,
    refreshGallery,
    confirmedAdminMints,
    view,
    onRequireLogin
  });
  const {
    loading,
    totalTokens,
    filteredNFTs,
    nfts,
    badges,
    lastMinted,
    lastTransfers,
    showModal,
    selectedNFT,
    searchQuery,
    setSearchQuery,
    networkFilter,
    setNetworkFilter,
    handleCardClick,
    handleModalClose,
    isWalletConnected,
    transferAddress,
    setTransferAddress,
    transferring,
    handleTransfer,
    transferError,
    transferSuccess,
    minting,
    handleMint,
    mintError,
    mintSuccess,
    truncateAddress,
    mainnetProvider,
    publicContract: galleryPublicContract,
    mintPrice,
    mintLimit,
    isSoldOut,
    remainingMints,
    turnstileToken,
    setTurnstileToken
  } = gallery;

  // totalTokens covers the live supply cap and every existing higher token ID.

  // -------- badge modal state --------
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);

  const handleBadgeClick = (badge) => {
    setSelectedBadge(badge);
    setShowBadgeModal(true);
  };

  const handleBadgeModalClose = () => {
    setShowBadgeModal(false);
    setSelectedBadge(null);
  };

  // -------- bridge hook --------
  const refreshNFTs = useCallback(() => {
    // Trigger a gallery refresh after bridge completes
    if (refreshGallery) refreshGallery();
  }, [refreshGallery]);

  const bridge = useBridge({
    account,
    signer,
    onRequireLogin,
    onBridgeStateChange,
    refreshNFTs,
  });

  // -------- main tab content --------
  let content;
  if (loading) {
    content = (
      <div className="text-center">
        <Spinner animation="border" variant="light" />
      </div>
    );
  } else if (view === 'analytics') {
    content = (
      <NFTAnalyticsView
        totalTokens={totalTokens}
        nfts={nfts}
        lastMinted={lastMinted}
        lastTransfers={lastTransfers}
        truncateAddress={truncateAddress}
        mainnetProvider={mainnetProvider}
        contractAddress={galleryPublicContract?.address}
        account={account}
        publicContract={galleryPublicContract}
        sogniClient={sogniClient}
      />
    );
  } else if (view === 'myNFTs') {
    content = (
      <NFTMyNFTsView
        nfts={nfts}
        badges={badges}
        account={account}
        sogniClient={sogniClient}
        handleCardClick={handleCardClick}
        handleBadgeClick={handleBadgeClick}
        baseURI={baseURI}
        publicContract={publicContract}
        // Bridge props
        bridgeSelectMode={bridge.bridgeSelectMode}
        selectedNFTs={bridge.selectedNFTs}
        sourceChain={bridge.sourceChain}
        onToggleSelection={bridge.toggleNFTSelection}
        onSelectAll={bridge.selectAllNFTs}
        onClearSelection={bridge.clearSelection}
        onEnterBridgeMode={() => bridge.setBridgeSelectMode(true)}
        onExitBridgeMode={bridge.exitBridgeMode}
        refreshTrigger={bridge.refreshTrigger}
        bridgedTokenKeys={bridge.bridgedTokenKeys}
      />
    );
  } else {
    content = (
      <NFTExplorerView
        filteredNFTs={filteredNFTs}
        baseURI={baseURI}
        handleCardClick={handleCardClick}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        accountCreated={accountCreated}
        networkFilter={networkFilter}
        setNetworkFilter={setNetworkFilter}
        isSoldOut={isSoldOut}
        remainingMints={remainingMints}
      />
    );
  }

  // -------- render --------
  return (
    <div>
      {content}

      {/* Bridge selection bar (floating at bottom) */}
      {bridge.bridgeSelectMode && bridge.selectedNFTs.size > 0 && (
        <BridgeSelectionBar
          selectedCount={bridge.selectedNFTs.size}
          totalEstimatedFee={bridge.totalEstimatedFee}
          feeQuoting={bridge.feeQuoting}
          feeError={bridge.feeError}
          nativeCurrencySymbol={bridge.nativeCurrencySymbol}
          destinationChain={bridge.destinationChain}
          onBridge={bridge.openBridgeModal}
          onCancel={bridge.exitBridgeMode}
        />
      )}

      {/* Bridge execution modal */}
      {bridge.showBridgeModal && (
        <BridgeModal
          show={bridge.showBridgeModal}
          onClose={bridge.closeBridgeModal}
          sourceChain={bridge.sourceChain}
          destinationChain={bridge.destinationChain}
          nativeCurrencySymbol={bridge.nativeCurrencySymbol}
          bridgeProgress={bridge.bridgeProgress}
          totalEstimatedFee={bridge.totalEstimatedFee}
          isBridging={bridge.isBridging}
          bridgeComplete={bridge.bridgeComplete}
          onStartBridge={bridge.startBridge}
          onRetry={bridge.retryBridge}
          lzStatuses={bridge.lzStatuses}
        />
      )}

      {showModal && selectedNFT && (
        <NFTDetailModal
          show={showModal}
          handleClose={handleModalClose}
          selectedNFT={selectedNFT}
          baseURI={baseURI}
          isWalletConnected={isWalletConnected}
          account={account}
          transferAddress={transferAddress}
          setTransferAddress={setTransferAddress}
          transferring={transferring}
          handleTransfer={handleTransfer}
          transferError={transferError}
          transferSuccess={transferSuccess}
          minting={minting}
          handleMint={handleMint}
          mintError={mintError}
          mintSuccess={mintSuccess}
          publicContract={publicContract}
          etherlinkPublicContract={etherlinkPublicContract}
          mintPrice={mintPrice}
          mintLimit={mintLimit}
          sogniClient={sogniClient}
          turnstileToken={turnstileToken}
          setTurnstileToken={setTurnstileToken}
          isSogniLoggedIn={isSogniLoggedIn}
          handleSogniLogin={handleSogniLogin}
          sogniUsername={sogniUsername}
        />
      )}

      {showBadgeModal && selectedBadge && (
        <BadgeDetailModal
          show={showBadgeModal}
          handleClose={handleBadgeModalClose}
          badge={selectedBadge}
        />
      )}
    </div>
  );
};

export default NFTGallery;
